import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { BaseLogo, getLogoOrgId } from "../../../../core/models/entities/BaseLogo";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";
import { useHealPendingHighRes } from "../../../../hooks/useHealPendingHighRes";
import { logoDetailKey } from "../../../../data/repositories/logoDetailCache";
import { enviroment } from "../../../../enviroment";

// Flag REACT_APP_LOGO_HEAL_PER_MUTATION - build-time constant, so the export
// at the bottom is fixed for the lifetime of the bundle and hook order never
// varies at runtime.
const healEnabled = enviroment.logoHealPerMutationEnabled;

type EditorLogoHeal = (
  logo: BaseLogo | null | undefined,
  isMutating: boolean,
  healToken: number,
  onDetail: (detail: BaseLogo) => void
) => void;

/**
 * Editor-level high-res heal (flag REACT_APP_LOGO_HEAL_PER_MUTATION), shared
 * by the standalone editor and the design editor's logo tool.
 *
 * Call budget: 0 new calls by construction. The detail subscription is only
 * enabled when the cache ALREADY holds the entry (every mutation seeds it:
 * upload via handleCreateLogo, colour/resize/copy via the repository, draft
 * save via LogoRepository.update) and its staleTime is Infinity, so enabling
 * it never triggers a fetch - only the heal's explicit invalidation does
 * (refetchType "all"), and that is capped at 3 GetLogo per `healToken`
 * (i.e. per mutation; the caller bumps the token after each one).
 *
 * `onDetail` fires with the cached detail whenever it CHANGES after the
 * subscription started and no mutation is in flight (`isMutating` false), so
 * the caller can patch the open logo (patchLogoFromDetail /
 * patchFormFromDetail). The value already in the cache when a logo is opened
 * is never delivered: the caller mounted with it (deep link, upload, clone)
 * or holds something newer (the design editor opens the DESIGN's copy of the
 * logo while the cache may hold a minutes-old detail entry from an earlier
 * visit) - pushing that back would sync the canvas and the colour list to
 * stale state. Only data that lands afterwards (mutation seeds, heal polls)
 * is by construction at least as new as what the editor shows. Held in a
 * ref: callers may pass a fresh closure every render.
 *
 * Budget accounting: the poll budget is per MOUNT and per `healToken`. A tool
 * opened on a cached entry that is still pending (e.g. the design editor's
 * clone flow, or a logo mutated moments ago in the standalone editor) polls
 * up to 3 times at open, even though no mutation happened in THIS tool.
 * Never fetches on mount/enable: the global refetchOnMount:false + staleTime
 * Infinity; the call sites remount per logo (see the design-editor wrapper)
 * so a key change while mounted - which react-query would refetch when the
 * entry has been invalidated - never happens.
 */
function useEditorLogoHealEnabled(
  logo: BaseLogo | null | undefined,
  isMutating: boolean,
  healToken: number,
  onDetail: (detail: BaseLogo) => void
): void {
  const queryClient = useQueryClient();
  const { logoRepository } = useRepositoriesContext();
  const logoId = logo?.id ?? 0;
  const orgId = logo ? getLogoOrgId(logo) : 0;
  const hasCachedEntry =
    logoId > 0 && queryClient.getQueryData(logoDetailKey(logoId)) !== undefined;

  const { data } = logoRepository.useLogo(orgId, logoId, {
    enabled: hasCachedEntry,
    staleTime: Infinity,
  });

  useHealPendingHighRes(hasCachedEntry ? logoId : undefined, data, healToken);

  const onDetailRef = useRef(onDetail);
  onDetailRef.current = onDetail;

  // The last value delivered (or, for a freshly opened logo, the value that
  // was already cached and is therefore skipped) - see the doc comment.
  const deliveredRef = useRef<{ logoId: number; data: BaseLogo | undefined }>({
    logoId: 0,
    data: undefined,
  });

  useEffect(() => {
    if (!logoId) return;
    if (deliveredRef.current.logoId !== logoId) {
      deliveredRef.current = { logoId, data };
      return;
    }
    if (!data || data.id !== logoId) return;
    if (isMutating) return;
    if (deliveredRef.current.data === data) return;
    deliveredRef.current = { logoId, data };
    onDetailRef.current(data);
  }, [data, isMutating, logoId]);
}

// Flag off: no observer (not even a disabled one - no idle cache entry), no
// heal, no callback. The call sites keep one hook call either way.
function useEditorLogoHealDisabled(): void {}

export const useEditorLogoHeal: EditorLogoHeal = healEnabled
  ? useEditorLogoHealEnabled
  : useEditorLogoHealDisabled;
